import {
    CSSProperties,
    ReactElement,
    cloneElement,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useIsomorphicLayoutEffect } from '../Hooks/useIsomorphicLayoutEffect'

export const EASE_IN_OUT = 'cubic-bezier(0.4, 0, 0.2, 1)'
const EASE_OUT = 'cubic-bezier(0, 0, 0.2, 1)'
const SHARP = 'cubic-bezier(0.4, 0, 0.6, 1)'

export const FADE_DURATION = 225
export const GROW_DURATION = 200
export const SLIDE_ENTER = 225
export const SLIDE_EXIT = 195

const GROW_SCALE = 'scale(0.75, 0.5625)'

/**
 * Keeps a component mounted for `duration` after `open` goes false, so it can
 * play an exit transition on the way out.
 *
 * `entered` lands a frame after the mount: the resting styles have to paint
 * before the entered class arrives, or there is nothing to transition from.
 * Pass `appear: false` to skip that first animation and open in place.
 */
export const useMountTransition = (
    open: boolean,
    duration: number,
    appear = true,
) => {
    const [mounted, setMounted] = useState(open)
    const [entered, setEntered] = useState(appear ? false : open)

    useEffect(() => {
        if (open) {
            setMounted(true)
            return
        }
        setEntered(false)
        const timer = setTimeout(() => setMounted(false), duration)
        return () => clearTimeout(timer)
    }, [open, duration])

    useEffect(() => {
        if (!open || !mounted) return
        let second = 0
        const first = requestAnimationFrame(() => {
            second = requestAnimationFrame(() => setEntered(true))
        })
        return () => {
            cancelAnimationFrame(first)
            cancelAnimationFrame(second)
        }
    }, [open, mounted])

    return { mounted, entered }
}

interface TransitionProps {
    /** a single element — the transition styles are cloned onto it */
    children: ReactElement<any>
    in: boolean
    /** false animates nothing on mount, opening in whatever state `in` says */
    appear?: boolean
}

interface TimedTransitionProps extends TransitionProps {
    duration?: number
}

interface FadeProps extends TimedTransitionProps {
    /**
     * Fade on opacity only and pin `visibility: visible` on the child, which
     * wins over a hidden ancestor. For a child that has to stay laid out as
     * visible while it starts: iOS 17 WebKit interrupts an autoplaying <video>
     * whose renderer computes to `visibility: hidden` (its invisible-autoplay
     * rule), and the player SDK does not retry the resulting AbortError.
     * Pointer events are off while faded out, which hidden gave for free.
     */
    keepVisible?: boolean
}

export const Fade = (props: FadeProps) => {
    const {
        appear = true,
        children,
        duration = FADE_DURATION,
        in: inProp,
        keepVisible = false,
    } = props
    const { mounted: visible, entered } = useMountTransition(
        inProp,
        duration,
        appear,
    )

    return cloneElement(children, {
        style: {
            ...children.props.style,
            opacity: entered ? 1 : 0,
            transition: `opacity ${duration}ms ${EASE_IN_OUT}`,
            ...(keepVisible
                ? {
                      visibility: 'visible',
                      pointerEvents: visible
                          ? children.props.style?.pointerEvents
                          : 'none',
                  }
                : { visibility: visible ? undefined : 'hidden' }),
        } as CSSProperties,
    })
}

export const Grow = (props: TimedTransitionProps) => {
    const {
        appear = true,
        children,
        duration = GROW_DURATION,
        in: inProp,
    } = props
    const { mounted: visible, entered } = useMountTransition(
        inProp,
        duration,
        appear,
    )

    // the scale trails the fade, and on the way out it waits a third of it
    const scaleDuration = Math.round(duration * 0.666)
    const scaleDelay = entered ? 0 : Math.round(duration * 0.333)

    return cloneElement(children, {
        style: {
            ...children.props.style,
            opacity: entered ? 1 : 0,
            transform: entered ? 'none' : GROW_SCALE,
            transition: `opacity ${duration}ms ${EASE_IN_OUT}, transform ${scaleDuration}ms ${EASE_IN_OUT} ${scaleDelay}ms`,
            visibility: visible ? undefined : 'hidden',
        } as CSSProperties,
    })
}

type SlideDirection = 'up' | 'down' | 'left' | 'right'

/** the offset that parks the element just off the matching viewport edge */
const offScreenTransform = (direction: SlideDirection, rect: DOMRect) => {
    switch (direction) {
        case 'left':
            return `translateX(${window.innerWidth - rect.left}px)`
        case 'right':
            return `translateX(-${rect.left + rect.width}px)`
        case 'up':
            return `translateY(${window.innerHeight - rect.top}px)`
        default:
            return `translateY(-${rect.top + rect.height}px)`
    }
}

interface SlideProps extends TransitionProps {
    direction: SlideDirection
    onExited?(): void
}

export const Slide = (props: SlideProps) => {
    const { appear = true, children, direction, in: inProp, onExited } = props
    const { mounted: visible, entered } = useMountTransition(
        inProp,
        SLIDE_EXIT,
        appear,
    )
    const nodeRef = useRef<HTMLElement>(null)
    const wasVisible = useRef(visible)

    // every render, not just on `entered`: a keyed child remounts under us, and
    // the fresh node needs parking before it paints
    useIsomorphicLayoutEffect(() => {
        const node = nodeRef.current
        if (!node) return
        if (entered) {
            node.style.transform = 'none'
            return
        }
        // measure where the element belongs
        const { transition } = node.style
        node.style.transition = ''
        node.style.transform = ''
        const rect = node.getBoundingClientRect()
        node.style.transform = offScreenTransform(direction, rect)
        if (inProp) node.getBoundingClientRect()
        node.style.transition = transition
    })

    useEffect(() => {
        if (wasVisible.current && !visible) onExited?.()
        wasVisible.current = visible
    }, [onExited, visible])

    return cloneElement(children, {
        ref: nodeRef,
        style: {
            ...children.props.style,
            transition: entered
                ? `transform ${SLIDE_ENTER}ms ${EASE_OUT}`
                : `transform ${SLIDE_EXIT}ms ${SHARP}`,
            visibility: visible ? undefined : 'hidden',
        } as CSSProperties,
    })
}
